import * as React from 'react';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { withEventScope } from '@repo/event-processor';
import { themeStyle } from '@repo/ui/theming';

import RotateControl from './RotateControl';
import ScalingControl from './ScalingControl';
import { Point, Size, SimpleEditorProps, ScaleDirection } from './types';
import { usePointerEvents } from './usePointerEvents';
import {
  generateControlPointPath,
  isInside,
  rotateControlSize,
  rotatePoint,
} from './utils';

// the minimum width/height of an image
const minScale = 10;

const scaleCursorDirections: ScaleDirection[] = [
  'ne',
  'e',
  'se',
  's',
  'sw',
  'w',
  'nw',
  'n',
];
const getScaleCursorDirection = (
  initialValue: ScaleDirection,
  angle: number
): ScaleDirection => {
  const directionIndex = scaleCursorDirections.findIndex(
    (x) => initialValue === x
  );
  let offset;

  // the direction of the cursor should be changed every 45 degrees with offset of 22.5 degrees
  if (angle >= -22.5 && angle < 22.5) {
    offset = 0;
  } else if (angle >= 22.5 && angle < 67.5) {
    offset = 1;
  } else if (angle >= 67.5 && angle < 112.5) {
    offset = 2;
  } else if (angle >= 112.5 && angle < 157.5) {
    offset = 3;
  } else if (angle >= 157.5 || angle < -157.5) {
    offset = 4;
  } else if (angle >= -157.5 && angle < -112.5) {
    offset = 5;
  } else if (angle >= -112.5 && angle < -67.5) {
    offset = 6;
  } else {
    offset = 7;
  }

  return scaleCursorDirections[
    (directionIndex + offset) % scaleCursorDirections.length
  ];
};

// for the easier moving and scaling calculations we apply all the events to the non-rotated image
// and then use the native svg transform to show the elements
const SimpleEditor = forwardRef<SVGImageElement, SimpleEditorProps>(
  (props, ref) => {
    const svgRef = useRef<SVGSVGElement>(null);

    const [size, setSize] = useState<Size>({
      width: props.imageWidth,
      height: props.imageHeight,
    });
    const [center, setCenter] = useState<Point>({
      x: props.width / 2,
      y: props.height / 2,
    });

    // visual position, inverted y in svg
    const [leftTop, setLeftTop] = useState<Point>({
      x: props.width / 2 - props.imageWidth / 2,
      y: props.height / 2 - props.imageHeight / 2,
    });
    const [leftBottom, setLeftBottom] = useState<Point>({
      x: props.width / 2 - props.imageWidth / 2,
      y: props.height / 2 + props.imageHeight / 2,
    });
    const [rightTop, setRightTop] = useState<Point>({
      x: props.width / 2 + props.imageWidth / 2,
      y: props.height / 2 - props.imageHeight / 2,
    });
    const [rightBottom, setRightBottom] = useState<Point>({
      x: props.width / 2 + props.imageWidth / 2,
      y: props.height / 2 + props.imageHeight / 2,
    });

    // angle in degrees
    const [rotationAngle, setRotationAngle] = useState<number>(0);

    // we need to use pointer events coordinates according svg view box
    const getAreaStartPoint = useCallback((): Point => {
      if (!svgRef.current) {
        return { x: 0, y: 0 };
      }

      const { top, left } = svgRef.current.getBoundingClientRect();
      return { x: left, y: top };
    }, []);

    const updateControls = (newCenter: Point, newSize?: Size): void => {
      const halfWidth = (newSize?.width ?? size.width) / 2;
      const halfHeight = (newSize?.height ?? size.height) / 2;
      setLeftTop({ x: newCenter.x - halfWidth, y: newCenter.y - halfHeight });
      setLeftBottom({
        x: newCenter.x - halfWidth,
        y: newCenter.y + halfHeight,
      });
      setRightTop({ x: newCenter.x + halfWidth, y: newCenter.y - halfHeight });
      setRightBottom({
        x: newCenter.x + halfWidth,
        y: newCenter.y + halfHeight,
      });
    };

    const handlePointerMove = (
      point: Point | null,
      eventPoint: Point
    ): void => {
      const area = getAreaStartPoint();
      if (
        point &&
        isInside(eventPoint, area, { width: props.width, height: props.height })
      ) {
        setCenter(point);
        updateControls(point);
      }
    };

    const [onPointerDown, onPointerMove, onPointerUp] = usePointerEvents({
      center: center,
      setTransform: (x: Point) => x,
      moveTransform: (x: Point) => x,
      getAreaStartPoint: getAreaStartPoint,
      onMove: handlePointerMove,
    });

    const handleScale = (oppositePoint: Point, eventPoint: Point): void => {
      const ratio = size.width / size.height;

      let width: number;
      let height: number;
      if (eventPoint.y > oppositePoint.y) {
        width = Math.abs(eventPoint.x - oppositePoint.x);
        height = width / ratio;
      } else {
        height = oppositePoint.y - eventPoint.y;
        width = height * ratio;
      }

      if (width < minScale || height < minScale) {
        return;
      }

      // the center of rotation has moved, we should move the image so that
      // the opposite point should stay on the same place,
      // so we rotate old and new value and match them
      const scaledRotationCenter = {
        x:
          eventPoint.x > oppositePoint.x
            ? oppositePoint.x + width / 2
            : oppositePoint.x - width / 2,
        y:
          eventPoint.y > oppositePoint.y
            ? oppositePoint.y + height / 2
            : oppositePoint.y - height / 2,
      };

      const scaledRotatedOppositePoint = rotatePoint(
        oppositePoint,
        scaledRotationCenter,
        rotationAngle
      );
      const rotatedOppositePoint = rotatePoint(
        oppositePoint,
        center,
        rotationAngle
      );
      const offset = {
        x: scaledRotatedOppositePoint.x - rotatedOppositePoint.x,
        y: scaledRotatedOppositePoint.y - rotatedOppositePoint.y,
      };
      const newCenter = {
        x: scaledRotationCenter.x - offset.x,
        y: scaledRotationCenter.y - offset.y,
      };

      const newSize = { width, height };
      setCenter(newCenter);
      setSize(newSize);
      updateControls(newCenter, newSize);
    };

    const handleRotate = (eventPoint: Point): void => {
      const xOffset = eventPoint.x - center.x;
      const yOffset = eventPoint.y - center.y;
      const slope = xOffset / yOffset;
      // we want to have the full coordinates system
      let addDegrees = 0;
      if (xOffset > 0 && yOffset < 0) {
        addDegrees = -180;
      }
      if (xOffset < 0 && yOffset < 0) {
        addDegrees = 180;
      }
      const angleInRadians = -Math.atan(slope);
      const angleInDegrees = (angleInRadians * 180) / Math.PI + addDegrees;

      setRotationAngle(angleInDegrees);
    };

    const transformRotate = (
      point: Point,
      centerPoint?: { x: number; y: number },
      angle?: number
    ): Point => {
      centerPoint = centerPoint ?? {
        x: center.x,
        y: center.y,
      };
      angle = angle ?? -rotationAngle;
      return rotatePoint(point, centerPoint, angle);
    };

    const rotateStr = `rotate(${rotationAngle} ${center.x} ${center.y})`;

    return (
      <svg
        ref={svgRef}
        viewBox={`0 0 ${props.width} ${props.height}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <image
          ref={ref}
          cursor="move"
          height={size.height}
          href={props.image}
          transform={rotateStr}
          width={size.width}
          x={center.x - size.width / 2}
          y={center.y - size.height / 2}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
        <polygon
          fill="none"
          points={generateControlPointPath(center, {
            width: size.width / 2,
            height: size.height / 2,
          })}
          stroke={themeStyle.selectionBlue}
          strokeWidth={1}
          transform={rotateStr}
        />
        <ScalingControl
          center={leftTop}
          getAreaStartPoint={getAreaStartPoint}
          scaleDirection={getScaleCursorDirection('nw', rotationAngle)}
          transform={transformRotate}
          transformString={rotateStr}
          onMove={(eventPoint): void => {
            if (
              eventPoint.y <= rightBottom.y &&
              eventPoint.x <= rightBottom.x
            ) {
              handleScale(rightBottom, eventPoint);
            }
          }}
        />
        <ScalingControl
          center={leftBottom}
          getAreaStartPoint={getAreaStartPoint}
          scaleDirection={getScaleCursorDirection('sw', rotationAngle)}
          transform={transformRotate}
          transformString={rotateStr}
          onMove={(eventPoint): void => {
            if (eventPoint.y >= rightTop.y && eventPoint.x <= rightTop.x) {
              handleScale(rightTop, eventPoint);
            }
          }}
        />
        <ScalingControl
          center={rightTop}
          getAreaStartPoint={getAreaStartPoint}
          scaleDirection={getScaleCursorDirection('ne', rotationAngle)}
          transform={transformRotate}
          transformString={rotateStr}
          onMove={(eventPoint): void => {
            if (eventPoint.y <= leftBottom.y && eventPoint.x >= leftBottom.x) {
              handleScale(leftBottom, eventPoint);
            }
          }}
        />
        <ScalingControl
          center={rightBottom}
          getAreaStartPoint={getAreaStartPoint}
          scaleDirection={getScaleCursorDirection('se', rotationAngle)}
          transform={transformRotate}
          transformString={rotateStr}
          onMove={(eventPoint): void => {
            if (eventPoint.y >= leftTop.y && eventPoint.x >= leftTop.x) {
              handleScale(leftTop, eventPoint);
            }
          }}
        />
        <RotateControl
          center={{
            x: center.x,
            y: center.y + size.height / 2 + 12 + rotateControlSize,
          }}
          getAreaStartPoint={getAreaStartPoint}
          transform={transformRotate}
          transformString={rotateStr}
          onMove={handleRotate}
        />
      </svg>
    );
  }
);

SimpleEditor.displayName = 'SimpleEditor';

export default withEventScope(SimpleEditor, { module: 'simpleEditor' });
